import {AnimalMediaModel} from '../models/animal-media.model';
import {Component, Input} from '@angular/core';
import {RestService} from 'src/app/shared/services/rest.service';
import {combineLatest, take} from 'rxjs';
import {TranslocoService} from '@jsverse/transloco';
import {AnimalModel} from '../models/animal.model';
import {AnimalImagesRepository} from '../states/animal-images.repository';
import {WaitingDialogService} from 'src/app/shared/services/waiting-dialog.service';

@Component({
  selector: 'app-animal-details-timeline',
  templateUrl: './animal-details-timeline.component.html',
  styleUrls: ['./animal-details-timeline.component.scss'],
  standalone: false
})
export class AnimalDetailsTimelineComponent {
  @Input() animal: AnimalModel | null = null;
  events: any[] = [];
  animalImages: AnimalMediaModel[] = [];

  constructor(
    private readonly _restService: RestService,
    private readonly _translocoService: TranslocoService,
    private readonly _animalImagesRepository: AnimalImagesRepository,
    private readonly _waitingDialogService: WaitingDialogService
  ) {
  }

  loadData() {
    if (this.animal) {
      this._waitingDialogService.showWaitingDialog(true);
      combineLatest([
        this._restService.httpGet('/animals/' + this.animal?.id + '/history'),
        this._animalImagesRepository.getImages(this.animal?.id)
      ]).pipe(take(1)).subscribe(([history, images]) => {
        this.animalImages = images;
        this.processServerData(history);
        this._waitingDialogService.showWaitingDialog(false);
      });
    }
  }

  actionMap: {
    [key: string]: {
      [key: string]: (data: any) => string
    }
  } = {
    'create': {
      'App\\Entity\\Animal\\Animal': (data) => this._translocoService.translate('animal_created', {name: data.name}),
      'App\\Entity\\Animal\\AnimalImage': (data) => data.preview ? this._translocoService.translate('image_set_as_preview') : this._translocoService.translate('image_added'),
      'App\\Entity\\Animal\\AnimalWhereabouts': (data) => this._translocoService.translate('whereabouts_created', {date: data.date})
    },
    'update': {
      'App\\Entity\\Animal\\Animal': () => this._translocoService.translate('animal_property_changed'),
      'App\\Entity\\Animal\\AnimalImage': (data) => {
        if (data.preview) {
          return this._translocoService.translate('image_set_as_preview');
        } else if (data.categories) {
          return this._translocoService.translate('image_category_updated');
        } else {
          return this._translocoService.translate('image_added');
        }
      },
      'App\\Entity\\Animal\\AnimalOrigin': () => this._translocoService.translate('origin_property_changed'),
      'App\\Entity\\Animal\\AnimalWhereabouts': () => this._translocoService.translate('whereabouts_property_changed'),
    }
  };

  dataMap: {
    [key: string]: (data: any) => string
  } = {
    'App\\Entity\\Animal\\Animal': (data) => this.transformAnimalData(data),
    'App\\Entity\\Animal\\AnimalImage': (data) => this.transformAnimalImageData(data),
    'App\\Entity\\Animal\\AnimalOrigin': (data) => this.transformAnimalOriginData(data),
    'App\\Entity\\Animal\\AnimalWhereabouts': (data) => this.transformAnimalWhereaboutsData(data)
  };

  transformAnimalData(data: any) {
    const properties = [
      'category', 'type', 'bookNumber', 'breed', 'oldBookNumber', 'petPassportNumber', 'lifeNumber', 'transponderNumber',
      'sex', 'coatType', 'coatColor', 'size', 'receiptDate', 'name', 'weight', 'birthday', 'birthYear',
      'specialInformation', 'castratedDate', 'sterilizedDate', 'tattoo', 'tattooLocation', 'ringNumber', 'earTag',
      'hints', 'internalDetails', 'externalDetails', 'externalShortDescription', 'externalDescription',
      'syncWithWebsite', 'nameNew', 'nameOld', 'websiteMetaDescription', 'websiteMetaTitle', 'sanctuary', 'fosterHomeWanted'
    ];

    return this.transformData(data, properties, 'timelineAnimal');
  }

  transformAnimalImageData(data: any) {
    const properties = ['categories'];
    return this.transformData(data, properties, 'timelineAnimalImage');
  }

  transformAnimalOriginData(data: any) {
    const properties = [
      'dateOfReceipt', 'type', 'specialInformation', 'location', 'otherComments', 'conditionOfCare',
      'nutritionalStatus', 'unknownSubmittedPerson', 'submittedPersonsSalutation', 'submittedPersonsLastName',
      'submittedPersonsFirstName', 'submittedPersonsZipCode', 'submittedPersonsCity', 'submittedPersonsAddress',
      'submittedPersonsPhoneNumber', 'submittedPersonsMobilePhoneNumber', 'submittedPersonsMailAddress'
    ];

    return this.transformData(data, properties, 'timelineOrigin');
  }

  transformAnimalWhereaboutsData(data: any) {
    const properties = [
      'date', 'dateCare', 'information', 'externalDescription', 'deletedAt'
    ];

    return this.transformData(data, properties, 'timelineWhereabout');
  }

  transformData(data: any, properties: string[], translationPrefix: string): string {
    let result = '';

    properties.forEach(prop => {
      if (data.hasOwnProperty(prop) && data[prop] !== null && data[prop] !== undefined) {
        // Nur Properties verarbeiten, die im Data-Objekt vorhanden und nicht null sind
        const translatedProp = this._translocoService.translate(`${translationPrefix}.${prop}`);
        let value = data[prop];
        if (this.isDate(prop, value)) {
          const date = new Date(value);
          value = this.formatDate(date, prop);
        } else if (prop === 'type') {
          value = this._translocoService.translate(`animalAdd.origin.reasonForTransferSelect.${value.replace(/-/g, '_')}`);
        } else if (prop === 'sex') {
          value = this._translocoService.translate(`${value}`);
        } else if (prop === 'categories') {
          value = value.map((category: string) => this._translocoService.translate(`timelineImage.${category}`)).join(', ');
        }
        result += `${translatedProp}: ${value}<br>`;
      }
    });

    return result;
  }

  formatDate(date: Date, prop: string): string {
    if (prop === 'birthYear') {
      return date.getFullYear().toString();
    } else {
      return date.toLocaleDateString('de-DE', {day: '2-digit', month: '2-digit', year: 'numeric'});
    }
  }

  isDate(prop: string, value: string): boolean {
    const dateProps = ['date', 'dateOfReceipt', 'birthYear', 'birthday'];
    return dateProps.includes(prop) && !isNaN(Date.parse(value));
  }

  processServerData(serverData: any[]) {
    this.events = serverData?.reduce((events, item) => {
      if (this.shouldProcessItem(item)) {
        const event = this.createEvent(item);
        events.push(event);
      }
      return events;
    }, []);
  }

  shouldProcessItem(item: any): boolean {
    return !!this.actionMap[item.action] && !!this.actionMap[item.action][item.objectClass];
  }

  createEvent(item: any) {
    const status = this.getStatus(item);
    const event = {
      status: status,
      date: new Date(item.loggedAt).toLocaleString('de-DE'),
      image: this.getImage(item),
      imageId: item.objectClass.endsWith('AnimalImage') ? item.objectId : '',
      name: '',
      data: this.getData(item),
      icon: this.getIcon(item),
      backgroundColor: this.getBackgroundColor(item)
    };
    return event;
  }

  getData(item: any): string {
    if (this.dataMap[item.objectClass]) {
      return this.dataMap[item.objectClass](item.data);
    }
    return 'No data available';
  }

  getStatus(item: any): string {
    return this.actionMap[item.action][item.objectClass](item.data);
  }

  getImage(item: any): AnimalMediaModel | undefined {
    return item.objectClass.endsWith('AnimalImage') ? this.animalImages.find(image => image.id.toString() === item.objectId) : undefined;
  }

  getIcon(item: any): string {
    if (item.action === 'create') {
      return 'pi pi-plus';
    } else if ((item.objectClass.endsWith('Animal') || item.objectClass.endsWith('AnimalOrigin')) && item.action === 'update') {
      return 'pi pi-pen-to-square';
    } else if (item.objectClass.endsWith('AnimalImage')) {
      return 'pi pi-image';
    }
    return '';
  }

  getBackgroundColor(item: any): string {
    if (item.action === 'create') {
      return 'var(--p-primary-500)';
    } else if ((item.objectClass.endsWith('Animal') || item.objectClass.endsWith('AnimalOrigin')) && item.action === 'update') {
      return 'var(--p-secondary-500)';
    } else if (item.objectClass.endsWith('AnimalImage')) {
      return 'var(--p-primary-100)';
    }
    return '';
  }
}
